/**
 * Notification Service
 *
 * Client-side notification access and local delivery helpers for the unified
 * notifications system.
 *
 * Primary responsibilities:
 * - Load and update notification inbox rows and settings
 * - Trigger browser and in-app delivery for client-side notifications
 * - Call server routes for privileged actions such as email test delivery
 *
 * Dependencies:
 * - Supabase browser client
 * - Notification settings mappers
 */

import { supabase } from '@/lib/supabase/browser-client'
import type {
  Notification as NotificationEntity,
  NotificationSettings,
  NotificationChannel,
  NotificationType,
  NotificationPriority
} from '@/types/notifications'
import type { Tables } from '@/supabase/types'
import {
  buildDefaultNotificationSettings,
  buildNotificationSettingsInsert,
  buildNotificationSettingsUpdate,
  getNotificationPreferenceForType,
  mapNotificationRow,
  mapNotificationSettingsRow
} from '@/lib/notifications/settings'
import {
  buildQuietHoursDeferredData,
  getQuietHoursDeliveryDecision
} from '@/lib/notifications/quiet-hours'
import { recoverClientSession } from '@/lib/auth/client-session-recovery'
import { safeConsole } from '@/lib/utils/safeConsole'
import { toast } from 'sonner'

const NOTIFICATION_SETTINGS_CACHE_MS = 30_000
const NOTIFICATION_SETTINGS_ERROR_CACHE_MS = 5_000

type NotificationSettingsCacheEntry = {
  expiresAt: number
  settings: NotificationSettings
}

type NotificationSettingsErrorCacheEntry = {
  error: unknown
  expiresAt: number
}

type SupabaseQueryResult<T> = {
  data: T | null
  error: unknown
}

type NotificationRow = Tables<'notifications'>

function readErrorField(error: unknown, key: 'code' | 'message' | 'status') {
  if (!error || typeof error !== 'object' || !(key in error)) {
    return null
  }

  const value = (error as Record<typeof key, unknown>)[key]

  return typeof value === 'string' || typeof value === 'number'
    ? String(value).toLowerCase()
    : null
}

function isSupabaseAuthQueryError(error: unknown) {
  const status = readErrorField(error, 'status')
  const code = readErrorField(error, 'code')
  const message = readErrorField(error, 'message')
  const hasAuthMessage = Boolean(
    message &&
      (message.includes('jwt') ||
        message.includes('unauthorized') ||
        message.includes('invalid token'))
  )

  return (
    status === '401' ||
    status === '403' ||
    code === '401' ||
    code === '403' ||
    hasAuthMessage
  )
}

export class NotificationService {
  private static instance: NotificationService
  private permissionGranted = false
  private settingsCache = new Map<string, NotificationSettingsCacheEntry>()
  private settingsErrors = new Map<string, NotificationSettingsErrorCacheEntry>()
  private settingsRequests = new Map<string, Promise<NotificationSettings>>()

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService()
    }
    return NotificationService.instance
  }

  // Request desktop notification permission
  async requestDesktopPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      safeConsole.warn('This browser does not support desktop notifications')
      return false
    }

    if (Notification.permission === 'granted') {
      this.permissionGranted = true
      return true
    }

    if (Notification.permission === 'denied') {
      safeConsole.warn('Desktop notifications are blocked')
      return false
    }

    const permission = await Notification.requestPermission()
    this.permissionGranted = permission === 'granted'
    return this.permissionGranted
  }

  private getCachedSettings(userId: string) {
    const cached = this.settingsCache.get(userId)

    if (!cached) {
      return null
    }

    if (Date.now() > cached.expiresAt) {
      this.settingsCache.delete(userId)
      return null
    }

    return cached.settings
  }

  private getCachedSettingsError(userId: string) {
    const cached = this.settingsErrors.get(userId)

    if (!cached) {
      return null
    }

    if (Date.now() > cached.expiresAt) {
      this.settingsErrors.delete(userId)
      return null
    }

    return cached.error
  }

  private cacheSettings(userId: string, settings: NotificationSettings) {
    const cached = this.settingsCache.get(userId)
    const nextIsDefault = settings.id.startsWith('default:')
    const cachedIsDefault = cached?.settings.id.startsWith('default:') ?? false

    if (cached && nextIsDefault && !cachedIsDefault) {
      return
    }

    if (
      cached &&
      !nextIsDefault &&
      !cachedIsDefault &&
      settings.updatedAt.getTime() < cached.settings.updatedAt.getTime()
    ) {
      return
    }

    this.settingsCache.set(userId, {
      expiresAt: Date.now() + NOTIFICATION_SETTINGS_CACHE_MS,
      settings
    })
    this.settingsErrors.delete(userId)
  }

  private cacheSettingsError(userId: string, error: unknown) {
    this.settingsErrors.set(userId, {
      error,
      expiresAt: Date.now() + NOTIFICATION_SETTINGS_ERROR_CACHE_MS
    })
  }

  private async runAuthRecoverableQuery<T>(
    query: () => PromiseLike<SupabaseQueryResult<T>>
  ): Promise<SupabaseQueryResult<T>> {
    const result = await query()

    if (!isSupabaseAuthQueryError(result.error)) {
      return result
    }

    const recovery = await recoverClientSession({ forceRefresh: true }).catch(
      () => null
    )

    if (!recovery?.session?.user && !recovery?.serverAuthorized) {
      return result
    }

    return query()
  }

  private getNotificationSpaceId(
    notification: Pick<NotificationRow, 'data'>
  ) {
    const data = notification.data

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return null
    }

    const spaceId = (data as Record<string, unknown>).spaceId

    return typeof spaceId === 'string' && spaceId.trim().length > 0
      ? spaceId.trim()
      : null
  }

  private async filterNotificationRowsForActiveSpaces(
    notifications: NotificationRow[]
  ) {
    const spaceIds = Array.from(
      new Set(
        notifications
          .map(notification => this.getNotificationSpaceId(notification))
          .filter((spaceId): spaceId is string => Boolean(spaceId))
      )
    )

    if (spaceIds.length === 0) {
      return notifications
    }

    // Chunk to avoid PostgREST "URI too long" errors
    const CHUNK_SIZE = 200
    const allSpaceData: { id: string }[] = []
    for (let i = 0; i < spaceIds.length; i += CHUNK_SIZE) {
      const chunk = spaceIds.slice(i, i + CHUNK_SIZE)
      const { data, error } = await this.runAuthRecoverableQuery(() =>
        supabase
          .from('spaces')
          .select('id')
          .in('id', chunk)
          .is('deleted_at', null)
      )
      if (error) throw error
      if (data) allSpaceData.push(...data)
    }

    const activeSpaceIds = new Set(allSpaceData.map(space => space.id))

    return notifications.filter(notification => {
      const spaceId = this.getNotificationSpaceId(notification)

      return !spaceId || activeSpaceIds.has(spaceId)
    })
  }

  private async loadUserNotificationSettings(
    userId: string
  ): Promise<NotificationSettings> {
    try {
      const { data, error } = await this.runAuthRecoverableQuery(() =>
        supabase
          .from('notification_settings')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle()
      )

      if (error) {
        throw error
      }

      if (!data) {
        return await this.createDefaultSettings(userId)
      }

      return mapNotificationSettingsRow(data)
    } catch (error) {
      console.error('Failed to get notification settings:', error)
      throw error
    }
  }

  // Get user notification settings
  async getUserNotificationSettings(userId: string): Promise<NotificationSettings> {
    const cachedSettings = this.getCachedSettings(userId)

    if (cachedSettings) {
      return cachedSettings
    }

    const cachedError = this.getCachedSettingsError(userId)

    if (cachedError) {
      throw cachedError
    }

    const pendingRequest = this.settingsRequests.get(userId)

    if (pendingRequest) {
      return pendingRequest
    }

    const request = this.loadUserNotificationSettings(userId)
      .then((settings) => {
        this.cacheSettings(userId, settings)
        return settings
      })
      .catch((error) => {
        this.cacheSettingsError(userId, error)
        throw error
      })
      .finally(() => {
        if (this.settingsRequests.get(userId) === request) {
          this.settingsRequests.delete(userId)
        }
      })

    this.settingsRequests.set(userId, request)

    return request
  }

  async refreshUserNotificationSettings(userId: string): Promise<NotificationSettings> {
    this.settingsCache.delete(userId)
    this.settingsErrors.delete(userId)

    return this.getUserNotificationSettings(userId)
  }

  // Create default notification settings
  private async createDefaultSettings(userId: string): Promise<NotificationSettings> {
    try {
      const { data, error } = await this.runAuthRecoverableQuery(() =>
        supabase
          .from('notification_settings')
          .upsert(buildNotificationSettingsInsert(userId), {
            onConflict: 'user_id'
          })
          .select()
          .maybeSingle()
      )

      if (error) throw error

      return data
        ? mapNotificationSettingsRow(data)
        : buildDefaultNotificationSettings(userId)
    } catch (error) {
      console.error('Failed to create default settings:', error)
      throw error
    }
  }

  // Update notification settings
  async updateNotificationSettings(
    userId: string,
    settings: Partial<NotificationSettings>
  ): Promise<void> {
    try {
      await this.getUserNotificationSettings(userId)

      const updateData = buildNotificationSettingsUpdate(settings)

      const { error } = await this.runAuthRecoverableQuery(() =>
        supabase
          .from('notification_settings')
          .update(updateData)
          .eq('user_id', userId)
      )

      if (error) throw error

      this.settingsCache.delete(userId)
      this.settingsErrors.delete(userId)
    } catch (error) {
      console.error('Failed to update notification settings:', error)
      toast.error('Failed to update notification settings')
      throw error
    }
  }

  // Send notification
  async sendNotification(
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    data?: Record<string, unknown>,
    priority: NotificationPriority = 'medium',
    channels: NotificationChannel[] = ['in_app']
  ): Promise<void> {
    try {
      // Get user settings
      const settings = await this.getUserNotificationSettings(userId)

      // Check if this notification type is enabled
      const typeSettings = this.getTypeSettings(settings, type)
      if (!typeSettings.enabled) {
        return // Notification type is disabled
      }

      // Determine which channels to use
      const activeChannels = channels.filter((channel) => {
        if (!typeSettings.channels.includes(channel)) {
          return false
        }

        if (channel === 'desktop') {
          return settings.desktopNotifications.enabled
        }

        return true
      })

      if (activeChannels.length === 0) {
        return // No active channels
      }

      if (priority !== 'urgent') {
        const quietHoursDecision = getQuietHoursDeliveryDecision({
          quietHours: settings.quietHours
        })

        if (quietHoursDecision.active) {
          const { error } = await this.runAuthRecoverableQuery(() =>
            supabase
              .from('notifications')
              .insert({
                user_id: userId,
                type,
                title,
                message,
                data: buildQuietHoursDeferredData({
                  data: data || {},
                  decision: quietHoursDecision
                }),
                priority,
                channels: activeChannels,
                status: 'pending',
                scheduled_for: quietHoursDecision.resumeAt.toISOString()
              })
          )

          if (error) throw error
          return
        }
      }

      // Create notification record
      const { data: notification, error: createError } =
        await this.runAuthRecoverableQuery(() =>
          supabase
            .from('notifications')
            .insert({
              user_id: userId,
              type,
              title,
              message,
              data: data || {},
              priority,
              channels: activeChannels,
              status: 'pending'
            })
            .select()
            .single()
        )

      if (createError) throw createError

      const mappedNotification = mapNotificationRow(notification)
      let deliveredChannels = 0

      // Send through active channels
      for (const channel of activeChannels) {
        try {
          const delivered = await this.sendThroughChannel(
            channel,
            mappedNotification,
            settings
          )

          if (delivered) {
            deliveredChannels += 1
          }
        } catch (channelError) {
          safeConsole.error(
            'Failed to deliver notification through client channel',
            channel,
            notification.id,
            channelError
          )
        }
      }

      const { error: updateError } = await this.runAuthRecoverableQuery(() =>
        supabase
          .from('notifications')
          .update(
            deliveredChannels > 0
              ? {
                  status: 'sent',
                  sent_at: new Date().toISOString()
                }
              : {
                  status: 'failed'
                }
          )
          .eq('id', notification.id)
      )

      if (updateError) throw updateError
    } catch (error) {
      console.error('Failed to send notification:', error)
      throw error
    }
  }

  // Get type-specific settings
  private getTypeSettings(settings: NotificationSettings, type: NotificationType) {
    return getNotificationPreferenceForType(settings, type)
  }

  // Send notification through specific channel
  private async sendThroughChannel(
    channel: NotificationChannel,
    notification: NotificationEntity,
    settings: NotificationSettings
  ): Promise<boolean> {
    switch (channel) {
      case 'desktop':
        await this.sendDesktopNotification(notification, settings)
        return true
      case 'email':
        safeConsole.log(
          'Email delivery requires a server route; skipping client-side email send',
          notification.id
        )
        return false
      case 'in_app':
        await this.sendInAppNotification(notification)
        return true
    }
  }

  // Send desktop notification
  private async sendDesktopNotification(
    notification: NotificationEntity,
    settings: NotificationSettings
  ): Promise<void> {
    if (
      typeof window === 'undefined' ||
      !('Notification' in window) ||
      !settings.desktopNotifications.enabled
    ) {
      return
    }

    const hasPermission =
      this.permissionGranted || Notification.permission === 'granted'

    if (!hasPermission) {
      return
    }

    this.permissionGranted = true
    safeConsole.log('Desktop notification queued for realtime delivery', notification.id)
  }

  // Send in-app notification
  private async sendInAppNotification(notification: NotificationEntity): Promise<void> {
    // Show toast notification
    const toastType =
      notification.priority === 'urgent'
        ? 'error'
        : notification.priority === 'high'
          ? 'warning'
          : 'info'

    toast[toastType](notification.title, {
      description: notification.message,
      duration: notification.priority === 'urgent' ? 0 : 5000
    })
  }

  // Get user notifications
  async getUserNotifications(
    userId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<NotificationEntity[]> {
    try {
      const fetchLimit = Math.max(limit, 1) * 3
      const { data, error } = await this.runAuthRecoverableQuery(() =>
        supabase
          .from('notifications')
          .select('*')
          .eq('user_id', userId)
          .in('status', ['sent', 'delivered', 'read'])
          .order('created_at', { ascending: false })
          .range(offset, offset + fetchLimit - 1)
      )

      if (error) throw error

      const visibleRows = await this.filterNotificationRowsForActiveSpaces(
        (data || []) as NotificationRow[]
      )

      return visibleRows.slice(0, limit).map(mapNotificationRow)
    } catch (error) {
      console.error('Failed to get user notifications:', error)
      throw error
    }
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    try {
      const { error } = await this.runAuthRecoverableQuery(() =>
        supabase
          .from('notifications')
          .update({
            read_at: new Date().toISOString(),
            status: 'read'
          })
          .eq('id', notificationId)
      )

      if (error) throw error
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
      throw error
    }
  }

  // Mark all notifications as read
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const { error } = await this.runAuthRecoverableQuery(() =>
        supabase
          .from('notifications')
          .update({
            read_at: new Date().toISOString(),
            status: 'read'
          })
          .eq('user_id', userId)
          .in('status', ['sent', 'delivered'])
          .is('read_at', null)
      )

      if (error) throw error
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
      throw error
    }
  }

  // Get unread notification count
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const { data, error } = await this.runAuthRecoverableQuery(() =>
        supabase
          .from('notifications')
          .select('*')
          .eq('user_id', userId)
          .in('status', ['sent', 'delivered'])
          .is('read_at', null)
          .order('created_at', { ascending: false })
          .limit(1000)
      )

      if (error) throw error

      const visibleRows = await this.filterNotificationRowsForActiveSpaces(
        (data || []) as NotificationRow[]
      )

      return visibleRows.length
    } catch (error) {
      console.error('Failed to get unread count:', error)
      return 0
    }
  }

  // Schedule notification for later
  async scheduleNotification(
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    scheduledFor: Date,
    data?: Record<string, unknown>,
    priority: NotificationPriority = 'medium',
    channels: NotificationChannel[] = ['in_app']
  ): Promise<void> {
    try {
      const { error } = await this.runAuthRecoverableQuery(() =>
        supabase.from('notifications').insert({
          user_id: userId,
          type,
          title,
          message,
          data: data || {},
          priority,
          channels,
          status: 'pending',
          scheduled_for: scheduledFor.toISOString()
        })
      )

      if (error) throw error
    } catch (error) {
      console.error('Failed to schedule notification:', error)
      throw error
    }
  }

  async sendTestNotification(
    channels: NotificationChannel[]
  ): Promise<void> {
    const response = await fetch('/api/notifications/test', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ channels })
    })

    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as
        | { error?: string }
        | null

      throw new Error(payload?.error || 'Failed to send test notification')
    }
  }
}

// Export singleton instance
export const notificationService = NotificationService.getInstance()
